import { SiteUrls } from '@tableau/shared-urls';

// Enum for populating the origin param of the share URL. This param is used for attribution.
// Add more values here as we offer up the share URL in more places in the UI.
export enum ShareOrigin {
    CardLink = 'card_share_link',
}

// The params that will go in the share URL query string
const ShareUrlParamNames = {
    Embed: 'embed',
    Origin: 'origin'
};

export const viewShareUrl = (siteUrls: SiteUrls, viewPath: string, shareOrigin: ShareOrigin): string => {
    const path = viewPath.slice(viewPath[0] === '/' ? 1 : 0);
    // Remove the hash to make view urls deep linking friendly for mobile unless the url has a feature flag.
    let pathUrl = siteUrls.viewUrl(path);
    if (!pathUrl.includes('?')) {
      pathUrl = pathUrl.replace('/#/site/', '/t/').replace('/#/', '/');
    }
    return buildUrl(pathUrl, shareOrigin, {[ShareUrlParamNames.Embed]: 'n'});
};

export const projectShareUrl = (siteUrls: SiteUrls, projectId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.projectUrl(projectId);
    return buildUrl(pathUrl, shareOrigin);
};

export const workbookShareUrl = (siteUrls: SiteUrls, workbookId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.workbookUrl(workbookId);
    return buildUrl(pathUrl, shareOrigin);
};

export const datasourceShareUrl = (siteUrls: SiteUrls, dataSourceId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.datasourceUrl(dataSourceId);
    return buildUrl(pathUrl, shareOrigin);
};

export const flowShareUrl = (siteUrls: SiteUrls, flowId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.flowUrl(flowId);
    return buildUrl(pathUrl, shareOrigin);
};

export const metricShareUrl = (siteUrls: SiteUrls, metricId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.metricUrl(metricId);
    return buildUrl(pathUrl, shareOrigin);
};

export const publishedConnectionShareUrl = (siteUrls: SiteUrls, publishedConnectionId: string, shareOrigin: ShareOrigin): string => {
    const pathUrl = siteUrls.publishedConnectionUrl(publishedConnectionId);
    return buildUrl(pathUrl, shareOrigin);
};

// Builds the full share url from the content's pathUrl
// For example, given: `/#/views/path/foo` and some query params,
// it creates the full `http://origin.domain/#/views/path/foo?:origin=card_share_link&:embed=y` url.
const buildUrl = (contentPathUrl: string, shareOrigin: ShareOrigin, contentSpecificQueryParams: {[key: string]: string} = {}): string => {
    const queryParams = {
        ...defaultQueryParams(shareOrigin),
        ...contentSpecificQueryParams
    };
    return locationWrapper.origin() + contentPathUrl + queryString(queryParams);
};

// The default query parameters that are added to the query string for every object
const defaultQueryParams = (shareOrigin: ShareOrigin): {[key: string]: string} => {
    return {
        [ShareUrlParamNames.Origin]: shareOrigin,
    };
};

// Given an object of query parameter names and values, creates the query string
const queryString = (queryParams: {[key: string]: string}): string => {
    // Create query string from param object
    return '?' + Object.keys(queryParams).map(key => {
        return `:${key}=${queryParams[key]}`;
    }).join('&');
};

// Allows for stubbing of origin() in tests
export const locationWrapper = {
    // Returns the `https://hostname.com` part of the share URL
    origin: (): string => {
        // Simulate window.location.origin for IE. See: https://stackoverflow.com/questions/22564167/window-location-origin-gives-wrong-value-when-using-ie
        if (!window.location.origin) {
            return window.location.protocol + '//'
            + window.location.hostname
            + (window.location.port ? ':' + window.location.port : '');
        }
        return window.location.origin;
    },
};
